import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { useTheme, Box } from '@mui/material';
import type { InvestmentType, InvestmentTypeData } from '@inversor-hub/types';
import { PreviewOverlay } from './PreviewOverlay.tsx';

interface InvestmentTypePieChartProps {
  data: InvestmentTypeData[];
  height?: number;
  onStartInvesting?: () => void;
}

const investmentTypeLabelMap: Record<InvestmentType, string> = {
  TRADITIONAL_RENT: 'Alquiler Tradicional',
  ROOM_RENT: 'Alquiler por Habitaciones',
  ROOM_SALE: 'Venta por Habitaciones',
  FLIPPING: 'Compra-Venta',
  VACATIONAL: 'Alquiler Vacacional',
  CHANGE_OF_USE: 'Cambio de Uso',
};

const typeColors: Record<InvestmentType, string> = {
  TRADITIONAL_RENT: '#4caf50', // Green
  ROOM_RENT: '#2196f3', // Blue
  ROOM_SALE: '#00bcd4', // Cyan
  FLIPPING: '#ff9800', // Orange
  VACATIONAL: '#e91e63', // Pink
  CHANGE_OF_USE: '#9c27b0', // Purple
};

// Sample preview data to show what the chart will look like
const previewData: InvestmentTypeData[] = [
  { type: 'TRADITIONAL_RENT', count: 4, percentage: 50 },
  { type: 'ROOM_RENT', count: 2, percentage: 25 },
  { type: 'FLIPPING', count: 1, percentage: 12.5 },
  { type: 'VACATIONAL', count: 1, percentage: 12.5 },
];

export default function InvestmentTypePieChart({
  data,
  height = 350,
  onStartInvesting,
}: InvestmentTypePieChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const hasData = data.length > 0;
  const chartData = hasData ? data : previewData;

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const chartDataFormatted = chartData.map((item) => ({
      name: investmentTypeLabelMap[item.type],
      value: item.count,
      itemStyle: {
        color: hasData ? typeColors[item.type] : theme.palette.grey[400],
      },
      tooltip: {
        formatter: () => {
          return `
            <div style="font-weight: 600; margin-bottom: 8px;">${
              investmentTypeLabelMap[item.type]
            }</div>
            <div style="margin-bottom: 4px;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${
                hasData ? typeColors[item.type] : theme.palette.grey[400]
              }; border-radius: 50%; margin-right: 8px;"></span>
              Inversiones: ${item.count}
            </div>
            <div>
              Porcentaje: ${item.percentage.toFixed(1)}%
            </div>
          `;
        },
      },
    }));

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: theme.palette.divider,
        borderWidth: 1,
        textStyle: {
          color: theme.palette.text.primary,
          fontSize: 12,
        },
      },
      legend: {
        orient: 'horizontal',
        bottom: '10%',
        left: 'center',
        textStyle: {
          color: theme.palette.text.primary,
          fontSize: 12,
        },
        itemGap: 20,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: 8,
        padding: [8, 15],
      },
      series: [
        {
          name: 'Tipo de Inversión',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '45%'],
          data: chartDataFormatted,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          label: {
            show: false,
          },
          labelLine: {
            show: false,
          },
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [chartData, theme, hasData]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: `${height}px` }}>
      <div ref={chartRef} style={{ width: '100%', height: `${height}px` }} />
      {!hasData && (
        <PreviewOverlay
          title="Diversificación de Cartera"
          description="Visualiza cómo diversificas tu cartera entre diferentes tipos de inversión inmobiliaria."
          actionText="Explorar oportunidades"
          onAction={onStartInvesting}
        />
      )}
    </Box>
  );
}
